import React from 'react';
import styled from 'styled-components';
import { StyledProps } from '../types/styled-utils';

const FooterContainer = styled.footer`
  background-color: ${(props: StyledProps) => props.theme.colors.darkBg2};
  padding: 4rem 2rem 2rem;
  position: relative;
  
  @media (max-width: ${(props: StyledProps) => props.theme.breakpoints.md}) {
    padding: 3rem 1rem 2rem;
  }
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 2rem;
  
  @media (max-width: ${(props: StyledProps) => props.theme.breakpoints.md}) {
    flex-direction: column;
    text-align: center;
  }
`;

const FooterLeft = styled.div`
  flex: 1;
  min-width: 300px;
`;

const FooterLogo = styled.div`
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 1rem;
  
  span {
    color: ${(props: StyledProps) => props.theme.colors.primary};
  }
`;

const FooterDescription = styled.p`
  color: ${(props: StyledProps) => props.theme.colors.secondaryText};
  margin-bottom: 1.5rem;
  max-width: 400px;
  line-height: 1.6;
  
  @media (max-width: ${(props: StyledProps) => props.theme.breakpoints.md}) {
    max-width: 100%;
  }
`;

const FooterRight = styled.div`
  display: flex;
  gap: 3rem;
  
  @media (max-width: ${(props: StyledProps) => props.theme.breakpoints.md}) {
    flex-direction: column;
    gap: 1.5rem;
    width: 100%;
  }
`;

const FooterNav = styled.div`
  min-width: 140px;
`;

const FooterNavTitle = styled.h4`
  color: ${(props: StyledProps) => props.theme.colors.lightText};
  margin-bottom: 1rem;
  font-size: 1.1rem;
`;

const FooterNavLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
`;

const FooterLink = styled.a`
  color: ${(props: StyledProps) => props.theme.colors.secondaryText};
  transition: color 0.2s ease;
  
  &:hover {
    color: ${(props: StyledProps) => props.theme.colors.primary};
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
  
  @media (max-width: ${(props: StyledProps) => props.theme.breakpoints.md}) {
    justify-content: center;
  }
`;

const SocialIcon = styled.a`
  color: ${(props: StyledProps) => props.theme.colors.secondaryText};
  transition: color 0.2s ease;
  
  &:hover {
    color: ${(props: StyledProps) => props.theme.colors.primary};
  }
  
  svg {
    width: 24px;
    height: 24px;
  }
`;

const Divider = styled.div`
  height: 1px;
  background-color: rgba(255, 255, 255, 0.1);
  margin: 3rem 0 1.5rem;
`;

const FooterBottom = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: ${(props: StyledProps) => props.theme.breakpoints.md}) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const Copyright = styled.p`
  color: ${(props: StyledProps) => props.theme.colors.secondaryText};
  font-size: 0.9rem;
`;

const BackToTop = styled.a`
  color: ${(props: StyledProps) => props.theme.colors.primary};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  font-weight: 500;
  transition: color 0.2s ease;
  
  &:hover {
    color: ${(props: StyledProps) => props.theme.colors.secondary};
  }
`;

const Footer: React.FC = () => {
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  
  return (
    <FooterContainer>
      <FooterContent>
        <FooterLeft>
          <FooterLogo>
            <span>6</span>bit
          </FooterLogo>
          <FooterDescription>
            Music producer and developer creating digital experiences through sound and code.
          </FooterDescription>
          <SocialLinks>
            <SocialIcon 
              href="https://open.spotify.com/artist/6jSs5ecozKPDZ3LNFwlHH7?si=8vxujiP8QZWrALIyWMN6Bg"
              target="_blank"
              aria-label="Spotify"
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.371-.721.49-1.101.241-3.021-1.851-6.819-2.271-11.279-1.241-.418.101-.838-.16-.931-.579-.101-.418.16-.838.579-.931 4.901-1.121 9.13-.601 12.491 1.441.371.241.49.721.241 1.069zm1.481-3.291c-.301.46-.921.621-1.381.311-3.461-2.131-8.731-2.751-12.821-1.501-.491.15-1.021-.13-1.171-.621-.15-.491.13-1.021.621-1.171 4.671-1.421 10.471-.721 14.461 1.771.448.301.609.921.291 1.211zm.141-3.401c-4.151-2.471-11.021-2.701-15.001-1.491-.631.191-1.291-.16-1.481-.781-.191-.621.16-1.291.781-1.481 4.581-1.391 12.191-1.121 17.001 1.731.571.331.761 1.081.421 1.661-.331.571-1.081.761-1.721.361z" />
              </svg>
            </SocialIcon>
            
            <SocialIcon 
              href="https://www.youtube.com/channel/UCeYaEjzXGfBKx1uy7-r00jA"
              target="_blank"
              aria-label="YouTube"
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
              </svg>
            </SocialIcon>
            
            <SocialIcon 
              href="https://www.youtube.com/@modeofO"
              target="_blank"
              aria-label="YouTube Alternative"
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
              </svg>
            </SocialIcon>
          </SocialLinks>
        </FooterLeft>
        
        <FooterRight>
          <FooterNav>
            <FooterNavTitle>Navigation</FooterNavTitle>
            <FooterNavLinks>
              <FooterLink href="#home">Home</FooterLink>
              <FooterLink href="#music">Music</FooterLink>
              <FooterLink href="#terminal">Terminal</FooterLink>
              <FooterLink href="#about">About</FooterLink>
            </FooterNavLinks>
          </FooterNav>
          
          <FooterNav>
            <FooterNavTitle>Music</FooterNavTitle>
            <FooterNavLinks>
              <FooterLink href="https://open.spotify.com/artist/6jSs5ecozKPDZ3LNFwlHH7" target="_blank">Spotify</FooterLink>
              <FooterLink href="https://www.youtube.com/channel/UCeYaEjzXGfBKx1uy7-r00jA" target="_blank">YouTube</FooterLink>
              <FooterLink href="https://www.youtube.com/@modeofO" target="_blank">YouTube (alt)</FooterLink>
            </FooterNavLinks>
          </FooterNav>
        </FooterRight>
      </FooterContent>
      
      <Divider />
      
      <FooterBottom>
        <Copyright>© {new Date().getFullYear()} 6bit. All rights reserved.</Copyright>
        <BackToTop href="#" onClick={(e: React.MouseEvent<HTMLAnchorElement>) => { e.preventDefault(); scrollToTop(); }}>
          Back to top
          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2L5 12h6v10h2V12h6z" />
          </svg>
        </BackToTop>
      </FooterBottom>
    </FooterContainer>
  );
};

export default Footer; 